import React, { useEffect, useRef, useState } from 'react';
import { View, TouchableOpacity, Animated, Platform, StyleSheet } from 'react-native';
import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
// import { CCColors } from '@/Utils/CCColors';
import CCColors from '@/Utils/CCColors';
import CCButton from '@/Atoms/CCButton';

interface RenewalExpiryModalProps {
  days_to_expiry: number | null;
  state: 'normal' | 'warning' | 'urgent' | 'expired';
  onRenew: () => void;
  onClose: () => void;
}

const RenewalExpiryModal: React.FC<RenewalExpiryModalProps> = ({
  days_to_expiry,
  state,
  onRenew,
  onClose,
}) => {
  const shakeAnimation = useRef(new Animated.Value(0)).current;
  const pulseAnimation = useRef(new Animated.Value(0)).current;
  const badgePulseAnimation = useRef(new Animated.Value(0)).current;
  const waveAnimations = useRef([
    new Animated.Value(0),
    new Animated.Value(0),
    new Animated.Value(0),
  ]).current;

  useEffect(() => {
    // Start shake animation - use native driver only for mobile
    const shake = Animated.loop(
      Animated.sequence([
        Animated.timing(shakeAnimation, {
          toValue: 1,
          duration: 100,
          useNativeDriver: Platform.OS !== 'web',
        }),
        Animated.timing(shakeAnimation, {
          toValue: -1,
          duration: 100,
          useNativeDriver: Platform.OS !== 'web',
        }),
        Animated.timing(shakeAnimation, {
          toValue: 0,
          duration: 100,
          useNativeDriver: Platform.OS !== 'web',
        }),
      ])
    );
    shake.start();

    // Start pulse animation for calendar date
    const pulse = Animated.loop(
      Animated.sequence([
        Animated.timing(pulseAnimation, {
          toValue: 1,
          duration: 1500,
          useNativeDriver: Platform.OS !== 'web',
        }),
        Animated.timing(pulseAnimation, {
          toValue: 0,
          duration: 1500,
          useNativeDriver: Platform.OS !== 'web',
        }),
      ])
    );
    pulse.start();

    // Start badge pulse animation for warning/urgent/expired states
    if (state === 'warning' || state === 'urgent' || state === 'expired') {
      const badgePulse = Animated.loop(
        Animated.sequence([
          Animated.timing(badgePulseAnimation, {
            toValue: 1,
            duration: 2000,
            useNativeDriver: Platform.OS !== 'web',
          }),
          Animated.timing(badgePulseAnimation, {
            toValue: 0,
            duration: 2000,
            useNativeDriver: Platform.OS !== 'web',
          }),
        ])
      );
      badgePulse.start();
    }

    // Start wave animations for urgent state
    if (state === 'urgent') {
      waveAnimations.forEach((animation, index) => {
        Animated.loop(
          Animated.sequence([
            Animated.timing(animation, {
              toValue: 1,
              duration: 3000,
              delay: index * 1000,
              useNativeDriver: Platform.OS !== 'web',
            }),
            Animated.timing(animation, {
              toValue: 0,
              duration: 3000,
              useNativeDriver: Platform.OS !== 'web',
            }),
          ])
        ).start();
      });
    }

    return () => {
      shake.stop();
      pulse.stop();
      if (state === 'warning' || state === 'urgent' || state === 'expired') {
        badgePulseAnimation.stopAnimation();
      }
      if (state === 'urgent') {
        waveAnimations.forEach((animation) => {
          animation.stopAnimation();
        });
      }
    };
  }, [shakeAnimation, pulseAnimation, badgePulseAnimation, waveAnimations, state]);

  const getCalendarColor = () => {
    switch (state) {
      case 'expired':
      case 'urgent':
        return 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)';
      case 'warning':
        return 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)';
      case 'normal':
      default:
        return 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)';
    }
  };

  const getMessage = () => {
    if (days_to_expiry === null) return 'Your subscription status';

    if (days_to_expiry <= 0) {
      return 'Your subscription has expired';
    }
    return `Your subscription ends in ${days_to_expiry} days`;
  };

  const getCalendarTransform = () => {
    const shake = shakeAnimation.interpolate({
      inputRange: [-1, 0, 1],
      outputRange: ['-4deg', '0deg', '4deg'],
    });

    return [{ rotate: shake }];
  };

  const getDateScale = () => {
    return pulseAnimation.interpolate({
      inputRange: [0, 1],
      outputRange: [1, 1.1],
    });
  };

  const getBadgeScale = () => {
    return badgePulseAnimation.interpolate({
      inputRange: [0, 1],
      outputRange: [1, 1.15],
    });
  };

  const getWaveOpacity = (index: number) => {
    return waveAnimations[index].interpolate({
      inputRange: [0, 0.5, 1],
      outputRange: [0, 0.4, 0],
    });
  };

  const getWaveScale = (index: number) => {
    return waveAnimations[index].interpolate({
      inputRange: [0, 1],
      outputRange: [0.8, 1.5],
    });
  };

  // State for web animations (since we can't use Animated.Value._value directly)
  const [shakeValue, setShakeValue] = useState(0);
  const [pulseValue, setPulseValue] = useState(0);
  const [badgePulseValue, setBadgePulseValue] = useState(0);
  const [waveValues, setWaveValues] = useState([0, 0, 0]);

  // Listen to animation values for web
  useEffect(() => {
    if (Platform.OS === 'web') {
      let animationFrame: number;
      
      const updateAnimations = () => {
        // Access the internal _value property for web
        // @ts-ignore
        setShakeValue(shakeAnimation._value || 0);
        // @ts-ignore
        setPulseValue(pulseAnimation._value || 0);
        // @ts-ignore
        setBadgePulseValue(badgePulseAnimation._value || 0);
        setWaveValues(waveAnimations.map((anim) => {
          // @ts-ignore
          return anim._value || 0;
        }));
        
        animationFrame = requestAnimationFrame(updateAnimations);
      };
      
      animationFrame = requestAnimationFrame(updateAnimations);

      return () => {
        if (animationFrame) {
          cancelAnimationFrame(animationFrame);
        }
      };
    }
  }, [shakeAnimation, pulseAnimation, badgePulseAnimation, waveAnimations]);

  const getCalendarBackgroundColor = () => {
    switch (state) {
      case 'expired':
      case 'urgent':
        return CCColors.RedBg;
      case 'warning':
        return CCColors.Cancel.Pink;
      case 'normal':
      default:
        return CCColors.BlueAqua;
    }
  };

  const getCalendarTopColor = () => {
    switch (state) {
      case 'expired':
      case 'urgent':
        return CCColors.Error;
      case 'warning':
        return CCColors.Pink;
      case 'normal':
      default:
        return CCColors.Green;
    }
  };

  const calendarRotate = shakeAnimation.interpolate({
    inputRange: [-1, 0, 1],
    outputRange: ['-4deg', '0deg', '4deg'],
  });

  const dateScale = pulseAnimation.interpolate({
    inputRange: [0, 1],
    outputRange: [1, 1.1],
  });

  const badgeScale = badgePulseAnimation.interpolate({
    inputRange: [0, 1],
    outputRange: [1, 1.15],
  });

  return (
    <View style={styles.container}>
      {/* Alert waves for urgent state */}
      {/* {state === 'urgent' && (
        <View style={styles.waveContainer}>
          {[0, 1, 2].map((index) => {
            const waveOpacity = Platform.OS === 'web' 
              ? waveValues[index] > 0.5 
                ? (1 - waveValues[index]) * 0.5 
                : waveValues[index] * 0.5
              : getWaveOpacity(index);
            const waveScale = Platform.OS === 'web'
              ? 0.8 + waveValues[index] * 0.7
              : getWaveScale(index);
            
            return (
              <Animated.View
                key={index}
                style={[
                  styles.wave,
                  {
                    opacity: typeof waveOpacity === 'number' ? waveOpacity : getWaveOpacity(index),
                    transform: [{ 
                      scale: typeof waveScale === 'number' ? waveScale : getWaveScale(index) 
                    }],
                    ...(Platform.OS === 'web' && {
                      willChange: 'opacity, transform',
                    }),
                  },
                ]}
              />
            );
          })}
        </View>
      )} */}

      {/* Calendar Container */}
      <View style={styles.calendarWrapper}>
        <Animated.View
          style={[
            styles.calendar,
            {
              backgroundColor: getCalendarBackgroundColor(),
              transform: Platform.OS === 'web' 
                ? [{ rotate: `${shakeValue * 4}deg` }]
                : [{ rotate: calendarRotate }],
              ...(Platform.OS === 'web' && {
                willChange: 'transform',
              }),
            },
          ]}
        >
          {/* Calendar top */}
          <View
            style={[
              styles.calendarTop,
              {
                backgroundColor: getCalendarTopColor(),
              },
            ]}
          >
            <View style={styles.calendarRing} />
            <View style={styles.calendarRing} />
          </View>

          {/* Calendar date */}
          <View style={styles.calendarDateContainer}>
            <Animated.View
              style={{
                transform: Platform.OS === 'web'
                  ? [{ scale: 1 + pulseValue * 0.1 }]
                  : [{ scale: dateScale }],
                ...(Platform.OS === 'web' && {
                  willChange: 'transform',
                }),
              }}
            >
              <CCText
                style={[
                  styles.calendarDate,
                  {
                    color: getCalendarTopColor(),
                  },
                ]}
              >
                {days_to_expiry !== null ? Math.max(0, days_to_expiry) : 0}
              </CCText>
            </Animated.View>
          </View>
        </Animated.View>

        {/* Warning badge for warning/urgent/expired states - outside calendar */}
        {/* {(state === 'warning' || state === 'urgent' || state === 'expired') && (
          <Animated.View
            style={[
              styles.badge,
              {
                transform: Platform.OS === 'web'
                  ? [{ scale: 1 + badgePulseValue * 0.15 }]
                  : [{ scale: badgeScale }],
                ...(Platform.OS === 'web' && {
                  boxShadow: `0 4px 20px rgba(243, 42, 82, ${Math.min(1, 0.4 + badgePulseValue * 0.3)})`,
                  willChange: 'transform, box-shadow',
                }),
              },
            ]}
          >
            <CCText style={styles.badgeText}>!</CCText>
          </Animated.View>
        )} */}
      </View>

      {/* Message */}
      <View style={styles.messageContainer}>
        <CCText style={styles.title}>
          {state === 'expired' ? 'Plan Expired' : 'Plan Expiring Soon'}
        </CCText>
        <CCText style={styles.description}>
          {getMessage()}
        </CCText>
      </View>

      {/* Renew and Close buttons */}
      <View style={styles.buttonContainer}>
        <CCButton
          onPress={onRenew}
          style={[
            styles.button,
            styles.renewButton,
            // {
            //   backgroundColor: state === 'urgent' || state === 'expired' 
            //     ? CCColors.RedBg 
            //     : state === 'warning' 
            //     ? CCColors.Cancel.Pink 
            //     : CCColors.Blue,
            //   ...(Platform.OS === 'web' && {
            //     transition: 'background-color 0.3s ease, transform 0.2s ease',
            //     cursor: 'pointer',
            //   }),
            // },
          ]}
        >
          <CCText style={styles.buttonText}>Renew Now</CCText>
        </CCButton>

        <CCButton
          onPress={onClose}
          style={[
            styles.button,
            styles.closeButton,
            // {
            //   ...(Platform.OS === 'web' && {
            //     transition: 'background-color 0.3s ease, transform 0.2s ease',
            //     cursor: 'pointer',
            //   }),
            // },
          ]}
        >
          <CCText color={CCColors.White} style={styles.buttonText}>Close</CCText>
        </CCButton>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    padding: 20,
    width: '100%',
  },
  waveContainer: {
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
    width: 200,
    height: 200,
    marginBottom: -60,
  },
  wave: {
    position: 'absolute',
    width: 180,
    height: 180,
    borderRadius: 90,
    borderWidth: 3,
    borderColor: CCColors.RedBg,
  },
  calendarWrapper: {
    position: 'relative',
    marginBottom: 30,
    alignItems: 'center',
    justifyContent: 'center',
  },
  calendar: {
    width: 160,
    height: 160,
    borderRadius: 16,
    position: 'relative',
    shadowColor: CCColors.Black,
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.25,
    shadowRadius: 12,
    elevation: 10,
    overflow: 'hidden',
    ...(Platform.OS === 'web' && {
      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
    }),
  },
  calendarTop: {
    height: 40,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    alignItems: 'center',
  },
  calendarRing: {
    width: 10,
    height: 14,
    backgroundColor: CCColors.White,
    opacity: 0.9,
    borderRadius: 3,
  },
  calendarDateContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: CCColors.White,
  },
  calendarDate: {
    fontSize: 56,
    fontWeight: 'bold',
  },
  badge: {
    position: 'absolute',
    zIndex: 1000,
    top: -12,
    right: -12,
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: CCColors.RedBg,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: CCColors.RedBg,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.5,
    shadowRadius: 8,
    elevation: 8,
    ...(Platform.OS === 'web' && {
      boxShadow: `0 4px 20px ${CCColors.RedBg}99`,
    }),
  },
  badgeText: {
    color: CCColors.White,
    fontSize: 36,
    fontWeight: 'bold',

  },
  messageContainer: {
    alignItems: 'center',
    marginBottom: 32,
    paddingHorizontal: 20,
  },
  title: {
    fontSize: 26,
    fontWeight: 'bold',
    color: CCColors.TextColor.Dark,
    marginBottom: 10,
    textAlign: 'center',
  },
  description: {
    fontSize: 16,
    color: CCColors.TextColor.Grey,
    textAlign: 'center',
    lineHeight: 22,
  },
  buttonContainer: {
    flexDirection: 'row',
    gap: 12,
    width: '100%',
    paddingHorizontal: 0,
  },
  button: {
    flex: 1,
    borderRadius: 10,
    paddingVertical: 14,
    paddingHorizontal: 24,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 48,
    ...(Platform.OS === 'web' && {
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    }),
  },
  renewButton: {
    shadowColor: CCColors.Blue,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 6,
  },
  closeButton: {
    backgroundColor: CCColors.Grey.Grey,
    shadowColor: CCColors.Grey.Grey,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 6,
    elevation: 4,
  },
  buttonText: {
    // color: CCColors.White,
    fontSize: 16,
    fontWeight: '600',
  },
});

export default RenewalExpiryModal;